// The C-04 draft (Frontend §6.6): `base` is the saved settings (#23), `draft` is base plus the user's edits to
// clinic-owned fields. Pure functions, unit tested.
import type { DayHours, Holiday, PutBody, Settings } from './types'

/** Holidays added in the draft carry a local id until they're saved (the server assigns the real one). */
export const LOCAL_HOLIDAY_PREFIX = 'new-'
export const isLocalHoliday = (h: Holiday) => h.id.startsWith(LOCAL_HOLIDAY_PREFIX)

/** The clinic-owned part of the settings (API.md §3.3 "Ownership"): what a draft can change and a save sends. */
function clinicOwned(s: Settings) {
  return {
    rules: s.rules,
    profile: s.profile,
    visitTypes: s.visitTypes.map((v) => [v.id, v.enabled, v.durationOverrideMinutes, v.providerIds, v.telehealth]),
    locations: s.locations.map((l) => [l.id, l.visible, l.address]),
    providers: s.providers.map((p) => [p.id, p.visible, p.specialty, p.bio, p.color]),
    // Task 24 (C-14): the templates and who's picked.
    // Task 26: and the conflict line.
    pfMessages: [
      s.pfMessages.subject,
      s.pfMessages.body,
      s.pfMessages.conflictLine,
      s.pfMessages.contacts.map((c) => [c.id, c.isRecipient]),
    ],
  }
}

/** `dirty = !deepEqual(draft, base)` over the clinic-owned fields (Sources are outside the draft). */
export function isDirty(base: Settings, draft: Settings): boolean {
  return JSON.stringify(clinicOwned(base)) !== JSON.stringify(clinicOwned(draft))
}

/** The `PUT …/settings` body (API.md §6.11): the draft's version and its clinic-owned fields only. */
export function toPutBody(draft: Settings): PutBody {
  return {
    version: draft.version,
    rules: { ...draft.rules, visitReasons: [...draft.rules.visitReasons] },
    profile: {
      contact: { ...draft.profile.contact },
      hours: draft.profile.hours.map((h: DayHours) => ({ ...h })),
      holidays: draft.profile.holidays.map((h) => ({
        ...(isLocalHoliday(h) ? {} : { id: h.id }),
        date: h.date,
        name: h.name,
        locationIds: h.locationIds,
      })),
    },
    visitTypes: draft.visitTypes.map((v) => ({
      id: v.id,
      enabled: v.enabled,
      durationOverrideMinutes: v.durationOverrideMinutes,
      providerIds: v.providerIds,
      telehealth: v.telehealth,
    })),
    locations: draft.locations.map((l) => ({ id: l.id, visible: l.visible, address: l.address })),
    providers: draft.providers.map((p) => ({
      id: p.id,
      visible: p.visible,
      specialty: p.specialty,
      bio: p.bio,
      color: p.color,
    })),
    // Task 24 (C-14): only while the feature is on (omitted = unchanged). People removed in PF are never sent.
    ...(draft.pfMessages.enabled
      ? {
          pfMessages: {
            subject: draft.pfMessages.subject,
            body: draft.pfMessages.body,
            conflictLine: draft.pfMessages.conflictLine,
            recipientIds: draft.pfMessages.contacts.filter((c) => c.isRecipient && !c.removedInPf).map((c) => c.id),
          },
        }
      : {}),
  }
}

/**
 * After Refresh from PF (Frontend §6.6 step 2): `fresh` is the refreshed saved state. A clean draft becomes `fresh`.
 * A dirty one reloads PF-owned fields only: `fresh` with the draft's clinic-owned values copied back onto every row
 * that still exists, plus the draft's `rules`, `profile` and `version` (D-34: a refresh doesn't bump it). New rows
 * come in as returned (off / hidden); rows now "Removed in PF" keep their on/off state.
 */
export function rebaseOnRefresh(base: Settings, draft: Settings, fresh: Settings): Settings {
  if (!isDirty(base, draft)) return fresh
  const vt = new Map(draft.visitTypes.map((v) => [v.id, v]))
  const loc = new Map(draft.locations.map((l) => [l.id, l]))
  const prov = new Map(draft.providers.map((p) => [p.id, p]))
  const picked = new Map(draft.pfMessages.contacts.map((c) => [c.id, c.isRecipient]))
  return {
    ...fresh,
    version: draft.version,
    rules: draft.rules,
    profile: draft.profile,
    pfMessages: {
      ...fresh.pfMessages,
      subject: draft.pfMessages.subject,
      body: draft.pfMessages.body,
      conflictLine: draft.pfMessages.conflictLine,
      contacts: fresh.pfMessages.contacts.map((c) => ({ ...c, isRecipient: picked.get(c.id) ?? c.isRecipient })),
    },
    visitTypes: fresh.visitTypes.map((v) => {
      const d = vt.get(v.id)
      return d
        ? {
            ...v,
            enabled: d.enabled,
            durationOverrideMinutes: d.durationOverrideMinutes,
            providerIds: d.providerIds,
            telehealth: d.telehealth,
          }
        : v
    }),
    locations: fresh.locations.map((l) => {
      const d = loc.get(l.id)
      return d ? { ...l, visible: d.visible, address: d.address } : l
    }),
    providers: fresh.providers.map((p) => {
      const d = prov.get(p.id)
      return d ? { ...p, visible: d.visible, specialty: d.specialty, bio: d.bio, color: d.color } : p
    }),
  }
}
